import { useEffect } from 'react';
import { AutoComplete } from 'antd';
import styled from 'styled-components';
import { Circle } from '@phosphor-icons/react';
import { ActiveText, ColoredPill, MuteText } from '../../../elements';
import {
  packagingAutocompleteHook,
  useAutocompleteInput,
  useAutocompleteLoading,
  useDebounce,
} from '../../../hooks';
import { CommonUtility, theme } from '../../../utility';
import { AutocompleteSearchInput } from '../../../components';

const OptionContainer = styled.div`
  padding: 8px 12px;
`;

const PackagingMaterialName = styled(ActiveText)`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

function resultRenderer({
  search,
  _id,
  name,
  dimensions,
  unitsAvailable,
  status,
  packagingType,
}) {
  const showResult = string => {
    try {
      const highlighted = string.replace(
        new RegExp(search, 'gi'),
        d => `<strong>${d}</strong>`,
      );

      return <span dangerouslySetInnerHTML={{ __html: highlighted }} />;
    } catch (error) {
      return string;
    }
  };

  return (
    <OptionContainer key={_id}>
      <div className="d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center">
          <PackagingMaterialName title={name} className="mr-2">
            {showResult(name)}
          </PackagingMaterialName>
          <ColoredPill
            color={status === 'active' ? theme.colors.green : theme.colors.grey}
          >
            <Circle weight="fill" />
            {CommonUtility.toTitleCase(status)}
          </ColoredPill>
        </div>
        <div className="text-nowrap">
          <span>{CommonUtility.roundNumber(dimensions?.length)}</span>
          <MuteText className="mx-1">x</MuteText>
          <span>{CommonUtility.roundNumber(dimensions?.height)}</span>
          <MuteText className="mx-1">x</MuteText>
          <span>{CommonUtility.roundNumber(dimensions?.width)}</span>
          <MuteText className="ml-1">{dimensions?.uom}.</MuteText>
        </div>
      </div>
      <div className="d-flex align-items-center justify-content-between">
        <div className="d-flex">
          <MuteText>Packaing Type:</MuteText>
          <span className="ml-2">{packagingType}</span>
        </div>
        <div className="text-nowrap">
          <MuteText>Available:</MuteText>
          <span className="ml-2 mr-1">{unitsAvailable}</span>
          <MuteText>Units</MuteText>
        </div>
      </div>
    </OptionContainer>
  );
}

export function PackagingSearchInput({
  top,
  onResultClick,
  inputClassName,
  setTableSearch = () => {},
  warehouseId,
  clearInput = true,
  refreshKey,
}) {
  const { display, query, onSearch, onSelect, clear } =
    useAutocompleteInput(setTableSearch);

  const finalSearch = useDebounce(query, 1000, 0);

  const { data, loading } = packagingAutocompleteHook(warehouseId, finalSearch, 'active');
  const isSearching = useAutocompleteLoading(query, finalSearch, loading);

  const optionIds = (data ?? []).map(d => d._id).filter(Boolean);

  const options = (data ?? []).map(item => ({
    value: item._id,
    label: resultRenderer({ ...item, search: display }),
  }));

  const resetSearch = () => {
    clear();
    onResultClick({});
  };

  useEffect(() => {
    if (CommonUtility.isNotEmpty(refreshKey)) {
      resetSearch();
    }
  }, [refreshKey]);

  return (
    <AutoComplete
      className={inputClassName}
      style={{ width: '100%' }}
      value={display}
      options={options}
      filterOption={false}
      placeholder="Packaging Material Name"
      placement={top ? 'topLeft' : 'bottomLeft'}
      classNames={{ popup: { root: 'auto-search-dropdown' } }}
      onSearch={value => onSearch(value, optionIds)}
      onSelect={value => {
        const item = data.find(d => d._id === value);
        if (item?._id) {
          onSelect(clearInput ? '' : item.name, () => onResultClick(item));
        }
      }}
    >
      <AutocompleteSearchInput isSearching={isSearching} onClear={resetSearch} />
    </AutoComplete>
  );
}
